import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Brain, MapPin, Phone, Globe, Search, Loader2, ArrowLeft, BadgeCheck, Briefcase, Filter, Star, MessageCircle, ExternalLink, Calendar, Clock, Video, User, CheckCircle, CreditCard, Play, Eye, Crown, ChevronDown } from 'lucide-react';
import { trackPageView, trackWhatsAppClick, trackCallClick, trackMapsClick, trackPhoneReveal } from '../services/analytics';
import { toast } from 'sonner';
import { InfoBadge, ENFOQUES_INFO } from '../components/TreatmentInfo';

import { CHILEAN_REGIONS } from '../constants/chileanLocations';
import { API } from '../utils/api';


function getImageUrl(url) {
  if (!url) return null;
  if (url.includes('.emergent.host/api/') || url.includes('.emergentagent.com/api/')) {
    try { url = new URL(url).pathname; } catch(e) {}
  }
  if (url.startsWith('/api/')) {
    var base = process.env.REACT_APP_BACKEND_URL;
    if (base && !base.includes('localhost') && typeof window !== 'undefined' && !base.includes(window.location.hostname)) {
      base = window.location.origin;
    }
    return (base || '') + url;
  }
  return url;
}

// Infer gender from name for avatar colors
function inferGender(name) {
  if (!name) return 'neutral';
  var n = name.toLowerCase();
  if (n.includes('dra.') || n.includes('dra ')) return 'female';
  if (n.includes('dr.') || n.includes('dr ')) return 'male';
  var femaleNames = ['maria','maría','maritza','rosa','luz','claudia','andrea','patricia','carolina','valentina','camila','catalina','javiera','constanza','daniela','fernanda','gabriela','isabella','isidora','josefa','macarena','martina','paula','rocio','sofía','sofia','susana','vanessa','laura','carla','marcela','lorena','francisca','natalia','alejandra','pilar','carmen','marta','ana','antonia','barbara','bárbara','elena','florencia','magdalena','teresa','verónica','veronica','ximena','yolanda','silvia','cecilia','beatriz','alicia','viviana','sandra','fabiola','roxana','soledad','ingrid','karina','paola','monica','mónica','adriana','karen','nancy','janet','priscila'];
  var maleNames = ['alex','carlos','guillermo','wilson','tomas','tomás','rene','rené','maximiliano','jose','josé','pedro','pablo','jorge','ricardo','roberto','alberto','fernando','luis','miguel','manuel','francisco','diego','sebastian','sebastián','daniel','felipe','andrés','andres','eduardo','rodrigo','arturo','raul','raúl','victor','víctor','oscar','óscar','mario','javier','rafael','hugo','héctor','hector','nelson','ernesto','alejandro','claudio','marcos','cristian','cristián','germán','german','matías','matias','nicolás','nicolas','patricio','gonzalo','jaime','antonio','sergio','ignacio','gabriel','mauricio'];
  var parts = n.replace(/^(ps\.|dr\.|dra\.|lic\.|mg\.|phd\.?)\s*/i, '').split(' ');
  var firstName = parts[0] || '';
  if (femaleNames.includes(firstName)) return 'female';
  if (maleNames.includes(firstName)) return 'male';
  return 'neutral';
}

function AvatarPlaceholder({ name, size }) {
  var gender = inferGender(name);
  var colors = {
    female: { bg: 'bg-purple-100', icon: 'text-purple-500', ring: 'ring-purple-200' },
    male: { bg: 'bg-teal-100', icon: 'text-teal-600', ring: 'ring-teal-200' },
    neutral: { bg: 'bg-slate-100', icon: 'text-slate-400', ring: 'ring-slate-200' }
  };
  var c = colors[gender];
  var sizeClass = size === 'lg' ? 'w-32 h-32' : size === 'md' ? 'w-20 h-20' : 'w-16 h-16';
  var iconSize = size === 'lg' ? 'w-14 h-14' : size === 'md' ? 'w-10 h-10' : 'w-8 h-8';
  var initials = (name || '').replace(/^(Ps\.|Dr\.|Dra\.|Lic\.|Mg\.)\s*/i, '').split(' ').map(function(w) { return w[0]; }).filter(Boolean).slice(0, 2).join('').toUpperCase();
  
  return (
    <div className={'flex items-center justify-center rounded-full ring-2 ' + sizeClass + ' ' + c.bg + ' ' + c.ring}>
      {initials ? (
        <span className={'font-bold ' + c.icon + ' ' + (size === 'lg' ? 'text-3xl' : size === 'md' ? 'text-xl' : 'text-base')}>{initials}</span>
      ) : (
        <User className={iconSize + ' ' + c.icon} />
      )}
    </div>
  );
}

const cities = ['Santiago', 'Valparaíso', 'Concepción', 'La Serena', 'Antofagasta', 'Temuco', 'Rancagua', 'Talca', 'Arica', 'Puerto Montt'];
const professionalTypes = [
  { value: 'Terapeuta', label: 'Terapeuta' },
  { value: 'Psicólogo/a', label: 'Psicólogo/a' },
  { value: 'Psiquiatra', label: 'Psiquiatra' },
  { value: 'Consejero/a', label: 'Consejero/a' },
  { value: 'Trabajador/a Social', label: 'Trabajador/a Social' },
  { value: 'Coach', label: 'Coach' },
  { value: 'Médico', label: 'Médico' },
  { value: 'Enfermero/a', label: 'Enfermero/a' }
];

function getTypeLabel(v) {
  if (!v) return 'Profesional';
  // Check if it's already a Spanish label
  var found = professionalTypes.find(function(t) { return t.value === v || t.label === v; });
  if (found) return found.label;
  // Handle legacy English values
  var legacyMap = {
    'therapist': 'Terapeuta',
    'psychologist': 'Psicólogo/a',
    'psychiatrist': 'Psiquiatra',
    'counselor': 'Consejero/a'
  };
  return legacyMap[v.toLowerCase()] || v;
}

function YouTubeEmbed({ url }) {
  if (!url) return null;
  
  let videoId = '';
  if (url.includes('youtube.com/watch')) {
    const urlParams = new URLSearchParams(url.split('?')[1]);
    videoId = urlParams.get('v');
  } else if (url.includes('youtu.be/')) {
    videoId = url.split('youtu.be/')[1]?.split('?')[0];
  }
  
  if (!videoId) return null;
  
  return (
    <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-slate-800">
      <iframe
        className="absolute inset-0 w-full h-full"
        src={`https://www.youtube.com/embed/${videoId}`}
        title="Video del profesional"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}

function StarRating({ rating, onRate, interactive = false, size = 'md' }) {
  var sizeClass = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-6 h-6' : 'w-5 h-5';
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map(function(star) {
        return (
          <button
            key={star}
            type={interactive ? 'button' : undefined}
            onClick={interactive ? function() { onRate(star); } : undefined}
            className={interactive ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'}
            disabled={!interactive}
          >
            <Star className={sizeClass + ' ' + (star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-600')} />
          </button>
        );
      })}
    </div>
  );
}

function WhatsAppButton({ number, name, targetType, targetId }) {
  if (!number) return null;
  var cleanNumber = number.replace(/\D/g, '');
  if (!cleanNumber.startsWith('56')) cleanNumber = '56' + cleanNumber;
  var message = encodeURIComponent('Hola ' + name + ', te encontré en SinAdicciones.org y me gustaría agendar una consulta contigo.');
  var url = 'https://wa.me/' + cleanNumber + '?text=' + message;
  
  function handleClick() {
    if (targetType && targetId) {
      trackWhatsAppClick(targetType, targetId);
    }
  }
  
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" onClick={handleClick} className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/30 rounded-xl hover:bg-green-500/20 transition-colors duration-200 text-green-400">
      <MessageCircle className="w-5 h-5" /><span>WhatsApp</span>
    </a>
  );
}

function GoogleMapsLink({ address, city, targetType, targetId }) {
  if (!address && !city) return null;
  var query = encodeURIComponent((address || '') + ', ' + (city || '') + ', Chile');
  var url = 'https://www.google.com/maps/search/?api=1&query=' + query;
  
  function handleClick() {
    if (targetType && targetId) {
      trackMapsClick(targetType, targetId);
    }
  }
  
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" onClick={handleClick} className="flex items-center gap-3 p-4 bg-slate-800 rounded-xl hover:bg-slate-700 transition-colors duration-200 text-slate-300">
      <MapPin className="w-5 h-5 text-emerald-400" /><span>Ver en Google Maps</span><ExternalLink className="w-4 h-4 ml-auto" />
    </a>
  );
}

function ReviewForm({ targetType, targetId, onSuccess }) {
  var [name, setName] = useState('');
  var [rating, setRating] = useState(0);
  var [comment, setComment] = useState('');
  var [loading, setLoading] = useState(false);
  var [error, setError] = useState('');
  var [success, setSuccess] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) { setError('Ingresa tu nombre'); return; }
    if (rating === 0) { setError('Selecciona una calificación'); return; }
    setLoading(true);
    setError('');
    try {
      await axios.post(API + '/' + targetType + 's/' + targetId + '/reviews', {
        reviewer_name: name,
        rating: rating,
        comment: comment || null
      });
      setSuccess(true);
      setName('');
      setRating(0);
      setComment('');
      if (onSuccess) onSuccess();
    } catch (e) {
      setError('Error al enviar la reseña');
    }
    setLoading(false);
  }

  if (success) {
    return (
      <div className="p-6 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-center">
        <p className="text-emerald-400 font-medium">¡Gracias por tu reseña!</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="text-sm font-medium text-slate-400 mb-2 block">Tu nombre</label>
        <Input value={name} onChange={function(e) { setName(e.target.value); }} placeholder="Nombre" className="bg-slate-950 border-slate-700 text-slate-200" />
      </div>
      <div>
        <label className="text-sm font-medium text-slate-400 mb-2 block">Calificación</label>
        <StarRating rating={rating} onRate={setRating} interactive={true} size="lg" />
      </div>
      <div>
        <label className="text-sm font-medium text-slate-400 mb-2 block">Comentario (opcional)</label>
        <Textarea value={comment} onChange={function(e) { setComment(e.target.value); }} placeholder="Cuéntanos tu experiencia..." className="bg-slate-950 border-slate-700 text-slate-200 min-h-[100px]" />
      </div>
      {error && <p className="text-red-400 text-sm">{error}</p>}
      <Button type="submit" disabled={loading} className="w-full bg-emerald-500 hover:bg-emerald-600">
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
        Enviar reseña
      </Button>
    </form>
  );
}

function ReviewsList({ targetType, targetId }) {
  var [reviews, setReviews] = useState([]);
  var [loading, setLoading] = useState(true);
  var [showAll, setShowAll] = useState(false);
  var VISIBLE_COUNT = 5;

  useEffect(function() {
    loadReviews();
  }, [targetType, targetId]);

  async function loadReviews() {
    try {
      var res = await axios.get(API + '/' + targetType + 's/' + targetId + '/reviews');
      setReviews(res.data.reviews || []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }

  if (loading) return <div className="flex justify-center py-4"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>;
  if (reviews.length === 0) return <p className="text-slate-500 text-center py-4">Aún no hay reseñas</p>;

  var visibleReviews = showAll ? reviews : reviews.slice(0, VISIBLE_COUNT);
  var hasMore = reviews.length > VISIBLE_COUNT;

  return (
    <div className="space-y-4">
      {visibleReviews.map(function(review) {
        return (
          <div key={review.review_id} className="p-4 bg-slate-800/50 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="font-medium text-slate-200">{review.reviewer_name}</span>
                {(review.source === 'google_places' || review.source === 'google') && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs font-medium rounded-full">
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Google
                  </span>
                )}
                {review.source === 'doctoralia' && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-500/20 text-teal-400 text-xs font-medium rounded-full">
                    Doctoralia
                  </span>
                )}
              </div>
              <StarRating rating={review.rating} size="sm" />
            </div>
            {review.relative_time && (
              <p className="text-slate-500 text-xs mb-2">{review.relative_time}</p>
            )}
            {review.comment && <p className="text-slate-400 text-sm">{review.comment}</p>}
          </div>
        );
      })}
      {hasMore && !showAll && (
        <button 
          onClick={function() { setShowAll(true); }}
          className="w-full py-3 text-center text-emerald-400 hover:text-emerald-300 font-medium border border-slate-700 rounded-xl hover:bg-slate-800/50 transition-colors"
          data-testid="reviews-show-more"
        >
          Ver más reseñas ({reviews.length - VISIBLE_COUNT} más)
        </button>
      )}
      {showAll && hasMore && (
        <button 
          onClick={function() { setShowAll(false); }}
          className="w-full py-3 text-center text-slate-400 hover:text-slate-300 font-medium border border-slate-700 rounded-xl hover:bg-slate-800/50 transition-colors"
          data-testid="reviews-show-less"
        >
          Ver menos
        </button>
      )}
    </div>
  );
}

function AppointmentForm({ professionalId, professionalName }) {
  var [formData, setFormData] = useState({
    patient_name: '',
    patient_email: '',
    patient_phone: '',
    date: '',
    time_slot: '',
    modality: 'presencial',
    reason: ''
  });
  var [submitting, setSubmitting] = useState(false);
  var [submitted, setSubmitted] = useState(false);
  var [pricingInfo, setPricingInfo] = useState(null);
  var [pendingPayment, setPendingPayment] = useState(null);

  // Load professional pricing info
  useEffect(function() {
    async function loadPricing() {
      try {
        var res = await axios.get(API + '/professionals/public/' + professionalId);
        setPricingInfo({
          accepts_online_payment: res.data.accepts_online_payment || false,
          reservation_price: res.data.reservation_price || 15000,
          consultation_price: res.data.consultation_price
        });
      } catch (e) {
        console.error('Error loading pricing:', e);
      }
    }
    loadPricing();
  }, [professionalId]);

  // Generate available time slots
  var timeSlots = [];
  for (var h = 9; h <= 19; h++) {
    timeSlots.push((h < 10 ? '0' : '') + h + ':00');
    if (h < 19) timeSlots.push((h < 10 ? '0' : '') + h + ':30');
  }

  // Generate next 30 days for date selection
  var availableDates = [];
  var today = new Date();
  for (var i = 1; i <= 30; i++) {
    var d = new Date(today);
    d.setDate(today.getDate() + i);
    var dayOfWeek = d.getDay();
    // Exclude Sundays (0)
    if (dayOfWeek !== 0) {
      availableDates.push({
        value: d.toISOString().split('T')[0],
        label: d.toLocaleDateString('es-CL', { weekday: 'short', day: 'numeric', month: 'short' })
      });
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      var res = await axios.post(API + '/appointments', {
        professional_id: professionalId,
        patient_name: formData.patient_name,
        patient_email: formData.patient_email,
        patient_phone: formData.patient_phone,
        date: formData.date,
        time_slot: formData.time_slot,
        modality: formData.modality,
        reason: formData.reason || null
      });
      
      if (res.data.success) {
        // Check if professional accepts online payment
        if (pricingInfo && pricingInfo.accepts_online_payment) {
          // Create payment checkout
          try {
            var paymentRes = await axios.post(API + '/payments/checkout', {
              appointment_id: res.data.appointment_id,
              origin_url: window.location.origin
            });
            // Redirect to Stripe checkout
            window.location.href = paymentRes.data.checkout_url;
          } catch (payErr) {
            console.error('Payment error:', payErr);
            // If payment fails, show success anyway (appointment created without payment)
            setSubmitted(true);
            toast.success('Cita solicitada. El profesional te contactará para el pago.');
          }
        } else {
          setSubmitted(true);
          toast.success('Cita solicitada correctamente');
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al solicitar cita');
    }
    setSubmitting(false);
  }

  if (submitted) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-emerald-400" />
        </div>
        <h3 className="text-xl font-semibold text-slate-50 mb-2">¡Solicitud Enviada!</h3>
        <p className="text-slate-400 mb-4">
          Tu solicitud de cita ha sido enviada a {professionalName}.<br />
          Te notificaremos cuando confirme tu cita.
        </p>
        <Button onClick={function() { setSubmitted(false); setFormData({ patient_name: '', patient_email: '', patient_phone: '', date: '', time_slot: '', modality: 'presencial', reason: '' }); }} variant="outline" className="border-slate-700 text-slate-300">
          Solicitar otra cita
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label className="text-slate-300">Nombre completo *</Label>
          <Input 
            value={formData.patient_name}
            onChange={function(e) { setFormData(function(p) { return Object.assign({}, p, { patient_name: e.target.value }); }); }}
            required
            className="bg-slate-800 border-slate-700 text-slate-200"
            placeholder="Tu nombre"
          />
        </div>
        <div>
          <Label className="text-slate-300">Email *</Label>
          <Input 
            type="email"
            value={formData.patient_email}
            onChange={function(e) { setFormData(function(p) { return Object.assign({}, p, { patient_email: e.target.value }); }); }}
            required
            className="bg-slate-800 border-slate-700 text-slate-200"
            placeholder="tu@email.com"
          />
        </div>
        <div>
          <Label className="text-slate-300">Teléfono *</Label>
          <Input 
            value={formData.patient_phone}
            onChange={function(e) { setFormData(function(p) { return Object.assign({}, p, { patient_phone: e.target.value }); }); }}
            required
            className="bg-slate-800 border-slate-700 text-slate-200"
            placeholder="+56 9 1234 5678"
          />
        </div>
        <div>
          <Label className="text-slate-300">Fecha *</Label>
          <Select value={formData.date} onValueChange={function(v) { setFormData(function(p) { return Object.assign({}, p, { date: v }); }); }}>
            <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-200">
              <SelectValue placeholder="Seleccionar fecha" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
              {availableDates.map(function(d) {
                return <SelectItem key={d.value} value={d.value} className="text-slate-200">{d.label}</SelectItem>;
              })}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-slate-300">Hora *</Label>
          <Select value={formData.time_slot} onValueChange={function(v) { setFormData(function(p) { return Object.assign({}, p, { time_slot: v }); }); }}>
            <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-200">
              <SelectValue placeholder="Seleccionar hora" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
              {timeSlots.map(function(t) {
                return <SelectItem key={t} value={t} className="text-slate-200">{t}</SelectItem>;
              })}
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2">
          <Label className="text-slate-300">Modalidad *</Label>
          <Select value={formData.modality} onValueChange={function(v) { setFormData(function(p) { return Object.assign({}, p, { modality: v }); }); }}>
            <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-200">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="presencial" className="text-slate-200"><span className="flex items-center gap-2"><User className="w-4 h-4" />Presencial</span></SelectItem>
              <SelectItem value="videollamada" className="text-slate-200"><span className="flex items-center gap-2"><Video className="w-4 h-4" />Videollamada</span></SelectItem>
              <SelectItem value="telefonica" className="text-slate-200"><span className="flex items-center gap-2"><Phone className="w-4 h-4" />Telefónica</span></SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2">
          <Label className="text-slate-300">Motivo de consulta (opcional)</Label>
          <Textarea 
            value={formData.reason}
            onChange={function(e) { setFormData(function(p) { return Object.assign({}, p, { reason: e.target.value }); }); }}
            className="bg-slate-800 border-slate-700 text-slate-200"
            placeholder="Describe brevemente el motivo de tu consulta..."
            rows={3}
          />
        </div>
      </div>
      
      {/* Show pricing info if professional accepts online payment */}
      {pricingInfo && pricingInfo.accepts_online_payment && (
        <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
          <p className="text-sm text-emerald-400 font-medium mb-1">💳 Pago online disponible</p>
          <p className="text-xs text-slate-400">
            Reserva: ${(pricingInfo.reservation_price || 15000).toLocaleString('es-CL')} CLP
            {pricingInfo.consultation_price && (
              <span> • Consulta completa: ${pricingInfo.consultation_price.toLocaleString('es-CL')} CLP</span>
            )}
          </p>
        </div>
      )}
      
      <Button 
        type="submit" 
        disabled={submitting || !formData.date || !formData.time_slot}
        className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
      >
        {submitting ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Procesando...</>
        ) : pricingInfo && pricingInfo.accepts_online_payment ? (
          <><CreditCard className="w-4 h-4 mr-2" />Reservar y Pagar ${(pricingInfo.reservation_price || 15000).toLocaleString('es-CL')}</>
        ) : (
          <><Calendar className="w-4 h-4 mr-2" />Solicitar Cita</>
        )}
      </Button>
      <p className="text-xs text-slate-500 text-center">
        {pricingInfo && pricingInfo.accepts_online_payment 
          ? 'Serás redirigido a una pasarela de pago segura'
          : 'El profesional confirmará tu cita por email'
        }
      </p>
    </form>
  );
}

export function ProfessionalsDirectory() {
  var [searchParams] = useSearchParams();
  var [professionals, setProfessionals] = useState([]);
  var [loading, setLoading] = useState(true);
  var [search, setSearch] = useState('');
  var [region, setRegion] = useState(searchParams.get('region') || '');
  var [type, setType] = useState(searchParams.get('type') || '');
  var [page, setPage] = useState(1);
  var [totalPages, setTotalPages] = useState(1);
  var [total, setTotal] = useState(0);
  var [filtersOpen, setFiltersOpen] = useState(false);
  var [verifiedOnly, setVerifiedOnly] = useState(false);
  var [initialized, setInitialized] = useState(false);

  useEffect(function() {
    setInitialized(true);
  }, []);

  useEffect(function() {
    if (!initialized) return;
    async function load() {
      setLoading(true);
      try {
        var url = API + '/professionals/public?page=' + page + '&limit=12';
        if (search) url += '&search=' + encodeURIComponent(search);
        if (region && region !== 'all') url += '&region=' + encodeURIComponent(region);
        if (type && type !== 'all') url += '&professional_type=' + type;
        if (verifiedOnly) url += '&is_verified=true';
        var res = await axios.get(url);
        setProfessionals(res.data.professionals || []);
        setTotalPages(res.data.pages || 1);
        setTotal(res.data.total || 0);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    }
    load();
  }, [initialized, page, region, type, search, verifiedOnly]);

  function onSearch(e) {
    e.preventDefault();
    setPage(1);
  }

  function clearFilters() {
    setRegion('');
    setType('');
    setSearch('');
    setVerifiedOnly(false);
    setPage(1);
  }

  var hasActiveFilters = region || type || verifiedOnly;

  // Filters Sidebar Content
  function FiltersContent() {
    return (
      <div className="space-y-6">
        <div>
          <label className="text-sm font-medium text-slate-300 mb-2 block">Especialidad</label>
          <Select value={type || 'all'} onValueChange={function(v) { setType(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-200">
              <SelectValue placeholder="Todas" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="all" className="text-slate-300">Todas</SelectItem>
              {professionalTypes.map(function(t) { return <SelectItem key={t.value} value={t.value} className="text-slate-300">{t.label}</SelectItem>; })}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <label className="text-sm font-medium text-slate-300 mb-2 block">Región</label>
          <Select value={region || 'all'} onValueChange={function(v) { setRegion(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-200">
              <SelectValue placeholder="Todas" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="all" className="text-slate-300">Todas las regiones</SelectItem>
              {CHILEAN_REGIONS.map(function(r) { return <SelectItem key={r.value} value={r.value} className="text-slate-300">{r.label}</SelectItem>; })}
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center gap-2">
          <input 
            type="checkbox" 
            id="verified-prof-filter" 
            checked={verifiedOnly} 
            onChange={function(e) { setVerifiedOnly(e.target.checked); setPage(1); }} 
            className="w-4 h-4 accent-emerald-500" 
          />
          <label htmlFor="verified-prof-filter" className="text-sm text-slate-300 cursor-pointer flex items-center gap-1">
            <BadgeCheck className="w-4 h-4 text-emerald-400" />Solo verificados
          </label>
        </div>
        
        <Button onClick={clearFilters} variant="outline" className="w-full border-slate-700 text-slate-400 hover:text-slate-200">
          Limpiar filtros
        </Button>
      </div>
    );
  }

  // Professional Card Component
  function ProfessionalCard({ pro }) {
    return (
      <Link to={'/profesionales/' + pro.user_id}>
        <Card className={'bg-white border-slate-200 hover:border-emerald-500/50 hover:shadow-lg transition-all duration-300 h-full overflow-hidden group ' + ((pro.is_premium || pro.plan_level > 0) ? 'ring-2 ring-amber-400/50' : '')}>
          <CardContent className="p-5">
            {/* Premium Badge */}
            {(pro.is_premium || pro.plan_level > 0) && (
              <div className="flex items-center gap-1 text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 text-xs font-medium mb-3 w-fit">
                <Crown className="w-3 h-3" />
                <span>Premium</span>
              </div>
            )}
            
            <div className="flex items-center gap-4 mb-4">
              {/* Avatar */}
              <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0">
                {pro.picture || pro.profile_image ? (
                  <img 
                    src={getImageUrl(pro.profile_image || pro.picture)} 
                    alt={pro.name} 
                    className="w-full h-full object-cover"
                    onError={function(e) { e.target.style.display='none'; e.target.parentElement.classList.add('avatar-fallback'); }}
                  />
                ) : (
                  <AvatarPlaceholder name={pro.name} size="sm" />
                )}
              </div>
              
              {/* Info */}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-lg text-slate-800 group-hover:text-emerald-600 transition-colors truncate">
                  {pro.name}
                </h3>
                <p className="text-emerald-600 font-medium text-sm">
                  {getTypeLabel(pro.professional_type)}
                </p>
                {pro.city && (
                  <p className="text-slate-500 text-sm flex items-center gap-1 mt-1">
                    <MapPin className="w-3 h-3" /> {pro.city}
                  </p>
                )}
              </div>
            </div>
            
            {/* Rating */}
            {pro.average_rating > 0 && (
              <div className="flex items-center gap-2 mb-3">
                <div className="flex items-center gap-1 text-yellow-500">
                  <Star className="w-4 h-4 fill-yellow-400" />
                  <span className="font-medium text-slate-800">{pro.average_rating.toFixed(1)}</span>
                </div>
                <span className="text-slate-400 text-sm">({pro.total_reviews || 0} reseñas)</span>
                {pro.source && !pro.total_reviews && (
                  <span className="text-xs text-slate-400">vía {pro.source}</span>
                )}
              </div>
            )}
            
            {/* Badges */}
            <div className="flex flex-wrap gap-2">
              {pro.is_verified && (
                <span className="flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 rounded-md text-xs font-medium">
                  <BadgeCheck className="w-3 h-3" /> Verificado
                </span>
              )}
              {pro.specialization && (
                <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded-md text-xs">
                  {pro.specialization.length > 30 ? pro.specialization.substring(0, 30) + '...' : pro.specialization}
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      </Link>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>Profesionales en Adicciones - Psicólogos, Psiquiatras y Terapeutas Chile 2026 | Sin Adicciones</title>
        <meta name="description" content="Encuentra profesionales especializados en tratamiento de adicciones en Chile. Psicólogos, psiquiatras y terapeutas verificados con reseñas y calificaciones." />
        <meta name="keywords" content="psicólogo adicciones Chile, psiquiatra adicciones, terapeuta drogas, profesional rehabilitación" />
        <link rel="canonical" href="https://sinadicciones.org/profesionales" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/profesionales" />
        <meta property="og:title" content="Profesionales en Adicciones - Psicólogos, Psiquiatras y Terapeutas Chile" />
        <meta property="og:description" content="Encuentra profesionales especializados en tratamiento de adicciones en Chile. Verificados, con reseñas y calificaciones." />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Profesionales en Adicciones Chile" />
        <meta name="twitter:description" content="Psicólogos, psiquiatras y terapeutas verificados." />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "Profesionales en Adicciones en Chile",
          "description": "Directorio de psicólogos, psiquiatras y terapeutas especializados en adicciones",
          "numberOfItems": total,
          "itemListElement": professionals.slice(0, 10).map(function(pro, index) {
            return {
              "@type": "ListItem",
              "position": index + 1,
              "item": {
                "@type": pro.professional_type === 'psiquiatra' ? "Physician" : "ProfessionalService",
                "name": pro.name,
                "description": getTypeLabel(pro.professional_type) + " especializado en adicciones",
                "url": "https://sinadicciones.org/profesionales/" + pro.professional_id,
                "medicalSpecialty": "AddictionMedicine"
              }
            };
          })
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "¿Qué tipo de profesional necesito para tratar una adicción?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "El tipo de profesional depende de tu situación. Los psicólogos especializados en adicciones ofrecen terapia conductual y emocional. Los psiquiatras pueden recetar medicamentos cuando es necesario. Los terapeutas y consejeros brindan apoyo continuo en el proceso de recuperación. Lo ideal es una evaluación inicial para determinar el mejor enfoque para tu caso."
              }
            },
            {
              "@type": "Question",
              "name": "¿Cuánto cuesta una consulta con un profesional en adicciones en Chile?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Las consultas con profesionales en adicciones en Chile varían entre $30.000 y $80.000 por sesión, dependiendo de la especialidad y experiencia. Los psiquiatras suelen tener tarifas más altas que los psicólogos. Muchos profesionales ofrecen primera consulta con descuento y algunos aceptan FONASA o Isapre."
              }
            },
            {
              "@type": "Question",
              "name": "¿Cómo sé si un profesional está calificado para tratar adicciones?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Busca profesionales con formación específica en adicciones o medicina de las dependencias. Verifica que estén registrados en la Superintendencia de Salud. En SinAdicciones.org los profesionales verificados han sido validados y puedes ver sus calificaciones y reseñas de pacientes anteriores."
              }
            },
            {
              "@type": "Question",
              "name": "¿Se puede hacer terapia de adicciones online?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Sí, muchos profesionales ofrecen terapia online por videollamada, especialmente útil si no hay especialistas cerca de tu ubicación. La terapia online es efectiva para el seguimiento y apoyo continuo, aunque la evaluación inicial presencial suele ser recomendada. Filtra por modalidad 'Online' en nuestro directorio."
              }
            },
            {
              "@type": "Question",
              "name": "¿Cuántas sesiones necesito para superar una adicción?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "El número de sesiones varía según cada persona y tipo de adicción. Un tratamiento típico incluye sesiones semanales durante 3-6 meses como mínimo, seguido de seguimiento mensual. La recuperación es un proceso continuo y muchos pacientes mantienen contacto con su terapeuta a largo plazo para prevenir recaídas."
              }
            }
          ]
        })}</script>
      
      <Header />
      
      {/* Hero Section */}
      <section className="pt-20 md:pt-24 pb-6 md:pb-8 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-4 md:mb-6">
            <h1 className="text-2xl md:text-4xl font-bold text-slate-50 mb-2" data-testid="professionals-title">
              Profesionales en Adicciones
            </h1>
            <p className="text-sm md:text-base text-slate-400">
              Terapeutas, psicólogos y psiquiatras especializados en Chile
            </p>
          </div>
          
          {/* Search Form */}
          <form onSubmit={onSearch} className="max-w-3xl mx-auto">
            <div className="flex gap-2 md:gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 w-4 md:w-5 h-4 md:h-5 text-slate-500" />
                <Input
                  value={search}
                  onChange={function(e) { setSearch(e.target.value); }}
                  placeholder="Buscar profesionales..."
                  className="pl-9 md:pl-12 h-10 md:h-12 bg-slate-900 border-slate-700 text-slate-200"
                  data-testid="professionals-search-input"
                />
              </div>
              <Button type="submit" className="h-10 md:h-12 px-4 md:px-8 bg-emerald-500 hover:bg-emerald-600">
                <Search className="w-4 h-4 md:mr-2" /><span className="hidden md:inline">Buscar</span>
              </Button>
            </div>
          </form>
        </div>
      </section>
      
      {/* Main Content */}
      <section className="py-6 md:py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Filters Sidebar */}
            <aside className="lg:w-64 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <Collapsible open={filtersOpen} onOpenChange={setFiltersOpen} className="lg:hidden mb-4">
                  <CollapsibleTrigger asChild>
                    <Button variant="outline" className="w-full justify-between border-slate-700 text-slate-300">
                      <span className="flex items-center gap-2"><Filter className="w-4 h-4" /> Filtros {hasActiveFilters ? '•' : ''}</span>
                      <ChevronDown className={'w-4 h-4 transition-transform ' + (filtersOpen ? 'rotate-180' : '')} />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-4">
                    <FiltersContent />
                  </CollapsibleContent>
                </Collapsible>
                
                <div className="hidden lg:block">
                  <h3 className="text-lg font-semibold text-slate-50 mb-4 flex items-center gap-2">
                    <Filter className="w-5 h-5" /> Filtros
                  </h3>
                  <FiltersContent />
                </div>
              </div>
            </aside>
            
            {/* Results */}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-6">
                <p className="text-slate-400">
                  {loading ? 'Cargando...' : total + ' profesionales encontrados'}
                </p>
              </div>
              
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                </div>
              ) : professionals.length === 0 ? (
                <div className="text-center py-20">
                  <User className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-400 mb-2">No se encontraron profesionales</h3>
                  <p className="text-slate-500 mb-4">Intenta ajustar los filtros de búsqueda</p>
                  <Button onClick={clearFilters} variant="outline" className="border-slate-700 text-slate-300">
                    Limpiar filtros
                  </Button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="professionals-grid">
                    {professionals.map(function(pro) {
                      return <ProfessionalCard key={pro.user_id} pro={pro} />;
                    })}
                  </div>
                  
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex justify-center gap-2 mt-10">
                      <Button
                        onClick={function() { setPage(Math.max(1, page - 1)); }}
                        disabled={page === 1}
                        variant="outline"
                        className="border-slate-700 text-slate-300 disabled:opacity-50"
                      >
                        Anterior
                      </Button>
                      <span className="flex items-center px-4 text-slate-400">
                        Página {page} de {totalPages}
                      </span>
                      <Button
                        onClick={function() { setPage(Math.min(totalPages, page + 1)); }}
                        disabled={page === totalPages}
                        variant="outline"
                        className="border-slate-700 text-slate-300 disabled:opacity-50"
                      >
                        Siguiente
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - Visible for users and SEO */}
      <section className="container mx-auto px-4 pb-12 max-w-6xl">
        <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center">Preguntas Frecuentes sobre Profesionales en Adicciones</h2>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4 md:p-6">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="pro-1" className="border-slate-700">
                <AccordionTrigger className="text-left hover:no-underline text-slate-200 hover:text-emerald-400 text-sm md:text-base">
                  ¿Qué tipo de profesional necesito para tratar una adicción?
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 text-sm">
                  El tipo de profesional depende de tu situación. Los psicólogos especializados en adicciones ofrecen terapia conductual y emocional. Los psiquiatras pueden recetar medicamentos cuando es necesario. Los terapeutas y consejeros brindan apoyo continuo en el proceso de recuperación. Lo ideal es una evaluación inicial para determinar el mejor enfoque para tu caso.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="pro-2" className="border-slate-700">
                <AccordionTrigger className="text-left hover:no-underline text-slate-200 hover:text-emerald-400 text-sm md:text-base">
                  ¿Cuánto cuesta una consulta con un profesional en adicciones en Chile?
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 text-sm">
                  Las consultas con profesionales en adicciones en Chile varían entre $30.000 y $80.000 por sesión, dependiendo de la especialidad y experiencia. Los psiquiatras suelen tener tarifas más altas que los psicólogos. Muchos profesionales ofrecen primera consulta con descuento y algunos aceptan FONASA o Isapre.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="pro-3" className="border-slate-700">
                <AccordionTrigger className="text-left hover:no-underline text-slate-200 hover:text-emerald-400 text-sm md:text-base">
                  ¿Cómo sé si un profesional está calificado para tratar adicciones?
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 text-sm">
                  Busca profesionales con formación específica en adicciones o medicina de las dependencias. Verifica que estén registrados en la Superintendencia de Salud. En SinAdicciones.org los profesionales verificados han sido validados y puedes ver sus calificaciones y reseñas de pacientes anteriores.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="pro-4" className="border-slate-700">
                <AccordionTrigger className="text-left hover:no-underline text-slate-200 hover:text-emerald-400 text-sm md:text-base">
                  ¿Se puede hacer terapia de adicciones online?
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 text-sm">
                  Sí, muchos profesionales ofrecen terapia online por videollamada, especialmente útil si no hay especialistas cerca de tu ubicación. La terapia online es efectiva para el seguimiento y apoyo continuo, aunque la evaluación inicial presencial suele ser recomendada. Filtra por modalidad 'Online' en nuestro directorio.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="pro-5" className="border-slate-700">
                <AccordionTrigger className="text-left hover:no-underline text-slate-200 hover:text-emerald-400 text-sm md:text-base">
                  ¿Cuántas sesiones necesito para superar una adicción?
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 text-sm">
                  El número de sesiones varía según cada persona y tipo de adicción. Un tratamiento típico incluye sesiones semanales durante 3-6 meses como mínimo, seguido de seguimiento mensual. La recuperación es un proceso continuo y muchos pacientes mantienen contacto con su terapeuta a largo plazo para prevenir recaídas.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>
      </section>
      
      <Footer />
    </div>
  );
}

export function ProfessionalDetail() {
  var params = useParams();
  var professionalId = params.professionalId;
  var [professional, setProfessional] = useState(null);
  var [loading, setLoading] = useState(true);
  var [activeTab, setActiveTab] = useState('info');
  var [showPhone, setShowPhone] = useState(false);

  function handleRevealPhone() {
    setShowPhone(true);
    trackPhoneReveal('professional', professionalId);
  }

  useEffect(function() {
    async function load() {
      try {
        var res = await axios.get(API + '/professionals/public/' + professionalId);
        setProfessional(res.data);
        // Track page view
        trackPageView('professional', professionalId);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    }
    load();
  }, [professionalId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Header />
        <div className="flex justify-center py-40"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
      </div>
    );
  }

  if (!professional) {
    return (
      <div className="min-h-screen bg-slate-950">
        <title>Profesional no encontrado (404) | SinAdicciones.org</title>
        <meta name="robots" content="noindex, follow" />
        <meta name="description" content="El profesional que buscas no existe o ha sido removido del directorio." />
        <Header />
        <div className="flex flex-col items-center justify-center py-40">
          <Brain className="w-16 h-16 text-slate-700 mb-4" />
          <h2 className="text-2xl text-slate-50 mb-2">Profesional no encontrado</h2>
          <Button asChild variant="outline" className="mt-4 border-slate-700 text-slate-300 hover:bg-slate-800">
            <Link to="/profesionales"><ArrowLeft className="w-4 h-4 mr-2" />Volver</Link>
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  var seoDescription = professional.bio ? professional.bio.substring(0, 160) : (professional.specialization || 'Profesional especializado en adicciones') + ' en ' + (professional.city || 'Chile');
  var seoImage = professional.picture || professional.profile_image || 'https://sinadicciones.org/og-image.png';

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>{professional.name + ' - ' + getTypeLabel(professional.professional_type) + ' | Sin Adicciones'}</title>
        <meta name="description" content={seoDescription} />
        <meta property="og:title" content={professional.name + ' - ' + getTypeLabel(professional.professional_type)} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={'https://sinadicciones.org/profesionales/' + professionalId} />
        <meta property="og:image" content={seoImage} />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={professional.name + ' - ' + getTypeLabel(professional.professional_type)} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={seoImage} />
        <link rel="canonical" href={'https://sinadicciones.org/profesionales/' + professionalId} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": professional.professional_type === 'psiquiatra' ? "Physician" : "ProfessionalService",
          "name": professional.name,
          "jobTitle": getTypeLabel(professional.professional_type),
          "description": professional.bio || ("Profesional especializado en tratamiento de adicciones en " + (professional.city || "Chile")),
          "url": "https://sinadicciones.org/profesionales/" + professionalId,
          "image": professional.picture || professional.profile_image,
          "telephone": professional.phone,
          "worksFor": professional.institution ? { "@type": "Organization", "name": professional.institution } : undefined,
          "address": professional.city ? { "@type": "PostalAddress", "addressLocality": professional.city, "addressRegion": professional.region || "Chile", "addressCountry": "CL" } : undefined,
          "medicalSpecialty": "AddictionMedicine",
          "priceRange": professional.price ? ("$" + Number(professional.price).toLocaleString()) : "$$",
          "areaServed": professional.city || "Chile",
          "aggregateRating": professional.average_rating > 0 ? { "@type": "AggregateRating", "ratingValue": professional.average_rating, "reviewCount": professional.total_reviews } : undefined
        })}</script>
      
      <Header />
      <main className="pt-20 pb-16">
        <div className="bg-slate-900 border-b border-slate-800">
          <div className="max-w-4xl mx-auto px-4 md:px-8 py-3">
            <Button asChild variant="ghost" size="sm" className="text-slate-400 hover:text-emerald-400 hover:bg-transparent">
              <Link to="/profesionales"><ArrowLeft className="w-4 h-4 mr-2" />Volver al directorio</Link>
            </Button>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
          <Card className="bg-slate-900 border-slate-800 overflow-hidden">
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row items-start gap-6 mb-8">
                {professional.picture || professional.profile_image ? (
                  <img 
                    src={getImageUrl(professional.picture || professional.profile_image)} 
                    alt={professional.name} 
                    className="w-32 h-32 rounded-2xl object-cover ring-4 ring-slate-800"
                    onError={function(e) { e.target.style.display='none'; e.target.parentElement.classList.add('avatar-fallback'); }}
                  />
                ) : (
                  <AvatarPlaceholder name={professional.name} size="lg" />
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <h1 className="text-3xl font-bold text-slate-50" data-testid="professional-name">{professional.name}</h1>
                    {professional.is_verified && (
                      <span className="flex items-center gap-2 px-4 py-2 bg-emerald-500/10 text-emerald-400 rounded-lg text-sm font-medium">
                        <BadgeCheck className="w-4 h-4" />Verificado
                      </span>
                    )}
                  </div>
                  <p className="text-lg text-emerald-400 font-medium mb-2">{getTypeLabel(professional.professional_type)}</p>
                  {professional.license_number && <p className="text-sm text-slate-500 mb-3">Licencia: {professional.license_number}</p>}
                  {professional.average_rating > 0 && (
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-3">
                        <StarRating rating={Math.round(professional.average_rating)} size="lg" />
                        <span className="text-slate-400">{professional.average_rating.toFixed(1)} ({professional.total_reviews} reseñas)</span>
                      </div>
                      {professional.source && (
                        <p className="text-xs text-slate-500 flex items-center gap-1">
                          {professional.source === 'doctoralia' && (
                            <>
                              <span className="inline-block w-3 h-3 bg-teal-500 rounded-full"></span>
                              Calificación de Doctoralia
                            </>
                          )}
                          {professional.source === 'google' && (
                            <>
                              <svg className="w-3 h-3 text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                              </svg>
                              Calificación de Google
                            </>
                          )}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Tabs */}
              <div className="flex gap-2 border-b border-slate-800 mb-6 overflow-x-auto">
                <button onClick={function() { setActiveTab('info'); }} className={'px-4 py-3 font-medium transition-colors whitespace-nowrap ' + (activeTab === 'info' ? 'text-emerald-400 border-b-2 border-emerald-500' : 'text-slate-400 hover:text-slate-300')}>Información</button>
                {professional.youtube_video && (
                  <button onClick={function() { setActiveTab('video'); }} className={'px-4 py-3 font-medium transition-colors flex items-center gap-2 whitespace-nowrap ' + (activeTab === 'video' ? 'text-emerald-400 border-b-2 border-emerald-500' : 'text-slate-400 hover:text-slate-300')}><Play className="w-4 h-4" />Video</button>
                )}
                <button onClick={function() { setActiveTab('appointment'); }} className={'px-4 py-3 font-medium transition-colors flex items-center gap-2 whitespace-nowrap ' + (activeTab === 'appointment' ? 'text-emerald-400 border-b-2 border-emerald-500' : 'text-slate-400 hover:text-slate-300')}><Calendar className="w-4 h-4" />Agendar Cita</button>
                <button onClick={function() { setActiveTab('reviews'); }} className={'px-4 py-3 font-medium transition-colors flex items-center gap-2 whitespace-nowrap ' + (activeTab === 'reviews' ? 'text-emerald-400 border-b-2 border-emerald-500' : 'text-slate-400 hover:text-slate-300')}><Star className="w-4 h-4" />Reseñas</button>
              </div>

              {activeTab === 'video' && professional.youtube_video && (
                <div className="space-y-6">
                  <h3 className="text-xl font-semibold text-slate-50">Video de Presentación</h3>
                  <YouTubeEmbed url={professional.youtube_video} />
                </div>
              )}

              {activeTab === 'info' && (
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    {professional.bio && (
                      <div>
                        <h3 className="text-lg font-semibold text-slate-50 mb-4">Acerca de</h3>
                        <p className="text-slate-400 whitespace-pre-line leading-relaxed">{professional.bio}</p>
                      </div>
                    )}
                    
                    {/* Services offered */}
                    {professional.services && professional.services.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold text-slate-50 mb-4">Servicios que Ofrece</h3>
                        <div className="flex flex-wrap gap-2">
                          {professional.services.map(function(service, idx) {
                            return <span key={idx} className="px-3 py-1.5 bg-emerald-500/10 text-emerald-400 rounded-lg text-sm">{service}</span>;
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* Therapeutic Approaches from the approaches array */}
                    {professional.approaches && professional.approaches.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold text-slate-50 mb-4">Enfoques Terapéuticos</h3>
                        <div className="flex flex-wrap gap-2">
                          {professional.approaches.map(function(approach, idx) {
                            return <InfoBadge key={idx} tipo="enfoque" valor={approach} />;
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* Fallback: Show specialization text with detected approaches if no approaches array */}
                    {(!professional.approaches || professional.approaches.length === 0) && professional.specialization && (
                      <div>
                        <h3 className="text-lg font-semibold text-slate-50 mb-4">Especialización y Enfoque</h3>
                        <p className="text-slate-400 mb-3">{professional.specialization}</p>
                        <div className="flex flex-wrap gap-2">
                          {Object.keys(ENFOQUES_INFO).map(function(enfoque) {
                            var enfoqueNombre = ENFOQUES_INFO[enfoque].nombre.toLowerCase();
                            var specLower = professional.specialization.toLowerCase();
                            if (specLower.includes(enfoque) || specLower.includes(enfoqueNombre.split(' ')[0])) {
                              return <InfoBadge key={enfoque} tipo="enfoque" valor={enfoque} />;
                            }
                            return null;
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* Modality */}
                    {professional.modality && professional.modality.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold text-slate-50 mb-4">Modalidad de Atención</h3>
                        <div className="flex flex-wrap gap-2">
                          {professional.modality.map(function(mod, idx) {
                            var label = mod === 'presencial' ? 'Presencial' : mod === 'online' ? 'Online' : mod === 'domicilio' ? 'A domicilio' : mod;
                            return <span key={idx} className="px-3 py-1.5 bg-blue-500/10 text-blue-400 rounded-lg text-sm">{label}</span>;
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* Schedule */}
                    {professional.schedule && (
                      <div>
                        <h3 className="text-lg font-semibold text-slate-50 mb-4">Horario de Atención</h3>
                        <p className="text-slate-400">{professional.schedule}</p>
                      </div>
                    )}
                    
                    {/* Prices */}
                    {(professional.consultation_price || professional.session_price) && (
                      <div>
                        <h3 className="text-lg font-semibold text-slate-50 mb-4">Precios</h3>
                        <div className="space-y-2">
                          {professional.consultation_price && (
                            <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                              <span className="text-slate-300">Consulta</span>
                              <span className="text-emerald-400 font-semibold">${Number(professional.consultation_price).toLocaleString('es-CL')} CLP</span>
                            </div>
                          )}
                          {professional.session_price && (
                            <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                              <span className="text-slate-300">Sesión</span>
                              <span className="text-emerald-400 font-semibold">${Number(professional.session_price).toLocaleString('es-CL')} CLP</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {professional.institution && (
                      <div>
                        <h3 className="text-lg font-semibold text-slate-50 mb-4">Institución</h3>
                        <div className="flex items-center gap-2 text-slate-400">
                          <Briefcase className="w-5 h-5 text-emerald-400" />{professional.institution}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-slate-50 mb-4">Contacto</h3>
                    <WhatsAppButton number={professional.whatsapp} name={professional.name} targetType="professional" targetId={professionalId} />
                    {professional.phone && (
                      showPhone ? (
                        <a href={'tel:' + professional.phone} onClick={function() { trackCallClick('professional', professionalId); }} className="flex items-center gap-3 p-4 bg-slate-800 rounded-xl hover:bg-slate-700 transition-colors duration-200 text-slate-300">
                          <Phone className="w-5 h-5 text-emerald-400" /><span>{professional.phone}</span>
                        </a>
                      ) : (
                        <button onClick={handleRevealPhone} className="flex items-center gap-3 p-4 bg-emerald-600 hover:bg-emerald-700 rounded-xl transition-colors duration-200 text-white w-full">
                          <Eye className="w-5 h-5" /><span>Ver Número de Teléfono</span>
                        </button>
                      )
                    )}
                    {(professional.address || professional.city) && (
                      <GoogleMapsLink address={professional.address} city={professional.city} targetType="professional" targetId={professionalId} />
                    )}
                    {professional.website && (
                      <a href={professional.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-slate-800 rounded-xl hover:bg-slate-700 transition-colors duration-200 text-slate-300">
                        <Globe className="w-5 h-5 text-emerald-400" /><span>Sitio web</span><ExternalLink className="w-4 h-4 ml-auto" />
                      </a>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'appointment' && (
                <div className="max-w-lg mx-auto">
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-semibold text-slate-50 mb-2">Solicitar Cita con {professional.name}</h3>
                    <p className="text-slate-400">Completa el formulario y el profesional confirmará tu cita</p>
                  </div>
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-6">
                      <AppointmentForm professionalId={professionalId} professionalName={professional.name} />
                    </CardContent>
                  </Card>
                </div>
              )}

              {activeTab === 'reviews' && (
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-50 mb-4">Reseñas</h3>
                    <ReviewsList targetType="professional" targetId={professionalId} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-50 mb-4">Deja tu reseña</h3>
                    <Card className="bg-slate-900 border-slate-800">
                      <CardContent className="p-6">
                        <ReviewForm targetType="professional" targetId={professionalId} />
                      </CardContent>
                    </Card>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
}
